// Credit-document ↔ open-credits matching helpers.
//
// Open credits store `credit_total` as a NET amount (no VAT) — see
// utils/lineCalc.js `calcCreditTotal`, which builds it from the net unit
// price. A received חשבונית זיכוי / תעודת החזרה, on the other hand, carries a
// gross `total` (subtotal + vat). Comparing the net open-credit sum against
// the gross doc total made every match look off by exactly the VAT amount.
//
// These helpers force an apples-to-apples NET comparison: derive the doc's net
// basis from its subtotal (falling back to total ÷ (1+vat) for older docs that
// stored no subtotal), then compare it to the selected open credits' net sum.

import { round2, vatMultiplier } from './money';

// The net amount of a credit document to compare open credits against.
// Credit documents (חשבונית זיכוי / תעודת החזרה) are stored with NEGATIVE
// subtotal/vat/total, so everything is taken as a magnitude. Prefers the
// stored subtotal; if that's missing, recovers net exactly as |total| − |vat|;
// and only as a last resort assumes the configured VAT rate.
export function creditDocNetBasis({ subtotal, total, vat } = {}, vatRate = 18) {
  const sub = Math.abs(parseFloat(subtotal) || 0);
  if (sub > 0) return round2(sub);
  const t = Math.abs(parseFloat(total) || 0);
  const v = Math.abs(parseFloat(vat) || 0);
  if (t > 0 && v > 0) return round2(t - v);
  if (t > 0) return round2(t / vatMultiplier(vatRate));
  return 0;
}

// Compare the selected open credits' net sum to the doc's net basis.
// Returns the signed diff (selected − doc) and whether they match within
// half an agora.
export function creditMatch(selectedNetSum, netBasis) {
  const diff = round2((parseFloat(selectedNetSum) || 0) - (parseFloat(netBasis) || 0));
  return { diff, matches: Math.abs(diff) < 0.01 };
}
